import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, UserPlus, Sparkles, Send, Loader2, Globe, CheckCircle2, Mails, Reply, MailOpen, Ban, Mail, ClipboardList, PenLine, Download, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STAGES, stageMeta, gbp } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { PrepareEmailDialog } from "@/components/PrepareEmailDialog";
import { TeamAvatar } from "@/components/TeamAvatar";
import { FollowUpBadge } from "@/components/FollowUpBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "all");
  const [delTarget, setDelTarget] = useState(null);
  const [prepareQueue, setPrepareQueue] = useState([]);
  const [prepareIndex, setPrepareIndex] = useState(0);
  const [worklist, setWorklist] = useState({ count: 0, ids: [] });
  const [showWorklist, setShowWorklist] = useState(false);

  const load = () => {
    api.enquiries().then(setEnquiries);
    api.team().then(setTeam);
    api.worklist().then((w) => setWorklist({ count: w.count, ids: w.lead_ids || [] })).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const memberById = (id) => team.find((m) => m.id === id);

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      const matchQ = !q || `${e.business_name} ${e.contact_name} ${e.email}`.toLowerCase().includes(q.toLowerCase());
      const matchStage = stageFilter === "all" || e.stage === stageFilter;
      const matchSource = sourceFilter === "all" || (e.source || "manual") === sourceFilter;
      const matchWork = !showWorklist || worklist.ids.includes(e.id);
      return matchQ && matchStage && matchSource && matchWork;
    });
  }, [enquiries, q, stageFilter, sourceFilter, showWorklist, worklist]);

  const [sortDue, setSortDue] = useState(false);
  const rows = useMemo(() => {
    if (!sortDue) return filtered;
    const val = (e) => (e.accounts_next_due ? Date.parse(e.accounts_next_due) : Infinity);
    return [...filtered].sort((a, b) => val(a) - val(b));
  }, [filtered, sortDue]);

  const save = async (payload) => {
    if (editing) { await api.updateEnquiry(editing.id, payload); toast.success("Enquiry updated"); }
    else { await api.createEnquiry(payload); toast.success("Enquiry created"); }
    setOpen(false); setEditing(null); load();
  };

  const confirmDelete = async () => {
    await api.deleteEnquiry(delTarget.id);
    toast.success("Enquiry deleted");
    setDelTarget(null); load();
  };

  const convertToClient = async (e) => {
    await api.createClient({
      business_name: e.business_name, contact_name: e.contact_name, email: e.email,
      phone: e.phone, company_type: e.company_type, services: e.services, enquiry_id: e.id,
    });
    await api.updateEnquiry(e.id, { stage: "onboarding" });
    toast.success(`${e.business_name} added to clients & moved to onboarding`);
    load();
  };

  const [enrichingId, setEnrichingId] = useState(null);
  const [enrichAll, setEnrichAll] = useState({ running: false, done: 0, total: 0 });
  const [outreachTarget, setOutreachTarget] = useState(null);
  const [sendingOutreach, setSendingOutreach] = useState(false);

  const enrichOne = async (e) => {
    setEnrichingId(e.id);
    try { await api.enrichLead(e.id); toast.success(`${e.business_name} enriched`); load(); }
    catch (err) { toast.error("Enrichment failed"); }
    finally { setEnrichingId(null); }
  };

  const runEnrichAll = async () => {
    try {
      const res = await api.enrichAll(true);
      if (!res.accepted) { toast.message(res.message || "Nothing to enrich"); return; }
      toast.success(`Enriching ${res.queued} lead(s) in the background…`);
      setEnrichAll({ running: true, done: 0, total: res.queued });
      const poll = setInterval(async () => {
        try {
          const st = await api.enrichStatus();
          setEnrichAll(st);
          if (!st.running) { clearInterval(poll); load(); toast.success("Enrichment complete"); }
        } catch (err) { clearInterval(poll); }
      }, 4000);
    } catch (e) { toast.error("Could not start enrichment"); }
  };

  const confirmOutreach = async () => {
    if (!outreachTarget) return;
    setSendingOutreach(true);
    try {
      const res = await api.sendOutreach(outreachTarget.id);
      toast.success(`Outreach sent to ${res.to}`);
      setOutreachTarget(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send outreach");
    } finally { setSendingOutreach(false); }
  };

  const [bulkOut, setBulkOut] = useState({ running: false, done: 0, total: 0, sent: 0, failed: 0 });
  const runBulkOutreach = async () => {
    try {
      const res = await api.outreachBulk();
      if (!res.accepted) { toast.message(res.message || "Nothing to send"); return; }
      toast.success(`Emailing ${res.queued} lead(s) in the background…`);
      setBulkOut({ running: true, done: 0, total: res.queued, sent: 0, failed: 0 });
      const poll = setInterval(async () => {
        try {
          const st = await api.outreachStatus();
          setBulkOut(st);
          if (!st.running) { clearInterval(poll); load(); toast.success(`Outreach complete — ${st.sent} sent${st.failed ? `, ${st.failed} failed` : ""}`); }
        } catch (e) { clearInterval(poll); }
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start bulk outreach"); }
  };

  const markReplied = async (e) => {
    try { await api.markReplied(e.id); toast.success(`${e.business_name} marked as replied → Intro Call`); load(); }
    catch (err) { toast.error("Could not update"); }
  };

  const TIERS = { hot: "bg-rose-100 text-rose-700", warm: "bg-amber-100 text-amber-700", cold: "bg-slate-100 text-slate-500" };

  const [followup, setFollowup] = useState({ running: false, done: 0, total: 0, sent: 0, failed: 0 });
  const runFollowups = async () => {
    try {
      const res = await api.followupBulk();
      if (!res.accepted) { toast.message(res.message || "Nothing to send"); return; }
      toast.success(`Sending ${res.queued} follow-up(s) in the background…`);
      setFollowup({ running: true, done: 0, total: res.queued, sent: 0, failed: 0 });
      const poll = setInterval(async () => {
        try {
          const st = await api.followupStatus();
          setFollowup(st);
          if (!st.running) { clearInterval(poll); load(); toast.success(`Follow-ups complete — ${st.sent} sent`); }
        } catch (e) { clearInterval(poll); }
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start follow-ups"); }
  };

  const [aiDraft, setAiDraft] = useState({ running: false, done: 0, total: 0, drafted: 0, failed: 0 });
  const runAiDraftBulk = async () => {
    try {
      const res = await api.aiDraftBulk("hot_warm", false);
      if (!res.accepted) { toast.message(res.message || "Nothing to draft"); return; }
      toast.success(`AI-drafting ${res.queued} Hot/Warm lead(s) in the background…`);
      setAiDraft({ running: true, done: 0, total: res.queued, drafted: 0, failed: 0 });
      const poll = setInterval(async () => {
        try {
          const st = await api.aiDraftStatus();
          setAiDraft(st);
          if (!st.running) { clearInterval(poll); load(); toast.success(`AI drafts ready — ${st.drafted} drafted${st.failed ? `, ${st.failed} failed` : ""}`); }
        } catch (e) { clearInterval(poll); }
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start AI drafting"); }
  };

  const prepareTarget = prepareQueue[prepareIndex] || null;
  const openSinglePrepare = (e) => { setPrepareQueue([e]); setPrepareIndex(0); };
  const startBatch = () => {
    const queue = enquiries.filter((e) =>
      e.source === "companies_house" && e.stage === "new_enquiry" &&
      ["hot", "warm"].includes(e.score_tier) && e.ai_draft_at &&
      (e.email || e.discovered_email) && !e.opted_out && !(e.seq_step > 0));
    if (!queue.length) { toast.message("No drafted Hot/Warm leads left to send."); return; }
    setPrepareQueue(queue); setPrepareIndex(0);
  };
  const advancePrepare = () => {
    const next = prepareIndex + 1;
    load();
    if (next >= prepareQueue.length) { setPrepareQueue([]); setPrepareIndex(0); toast.success("Batch complete — nicely done"); }
    else { setPrepareIndex(next); }
  };
  const exportCsv = async () => {
    try {
      const blob = await api.exportOutreachCsv("hot_warm");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "pro-accountants-outreach.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported — import into your mail-merge tool");
    } catch (e) { toast.error("Could not export CSV"); }
  };

  return (
    <div className="space-y-6" data-testid="enquiries-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Enquiries</h1>
          <p className="mt-1 text-sm text-slate-500">Every website and inbound enquiry in one place.</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Button variant={showWorklist ? "default" : "outline"} onClick={() => setShowWorklist((v) => !v)} data-testid="worklist-toggle" className="rounded-full" style={showWorklist ? { backgroundColor: "hsl(var(--brand))", color: "#fff" } : {}}>
            <ClipboardList className="mr-1 h-4 w-4" />Worklist ({worklist.count})
          </Button>
          <Button variant="outline" onClick={runEnrichAll} disabled={enrichAll.running} data-testid="enrich-all-button" className="rounded-full">
            {enrichAll.running
              ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Enriching {enrichAll.done}/{enrichAll.total}</>
              : <><Sparkles className="mr-1 h-4 w-4 text-indigo-500" />Enrich all leads</>}
          </Button>
          <Button variant="outline" onClick={runBulkOutreach} disabled={bulkOut.running} data-testid="bulk-outreach-button" className="rounded-full">
            {bulkOut.running
              ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Emailing {bulkOut.done}/{bulkOut.total}</>
              : <><Mails className="mr-1 h-4 w-4 text-emerald-600" />Email inbound</>}
          </Button>
          <Button variant="outline" onClick={runFollowups} disabled={followup.running} data-testid="followup-button" className="rounded-full">
            {followup.running
              ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Following up {followup.done}/{followup.total}</>
              : <><Reply className="mr-1 h-4 w-4 text-sky-600" />Send follow-ups</>}
          </Button>
          <Button variant="outline" onClick={runAiDraftBulk} disabled={aiDraft.running} data-testid="ai-draft-bulk-button" className="rounded-full">
            {aiDraft.running
              ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Drafting {aiDraft.done}/{aiDraft.total}</>
              : <><PenLine className="mr-1 h-4 w-4 text-violet-600" />AI-draft Hot/Warm</>}
          </Button>
          <Button variant="outline" onClick={startBatch} data-testid="batch-send-button" className="rounded-full">
            <Send className="mr-1 h-4 w-4 text-emerald-600" />Batch send
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button" className="rounded-full">
            <Download className="mr-1 h-4 w-4 text-slate-600" />Export CSV
          </Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-enquiry-button" style={{ backgroundColor: "hsl(var(--brand))" }} className="rounded-full text-white">
            <Plus className="mr-1 h-4 w-4" /> New enquiry
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="enquiry-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, contact or email…" className="pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[180px]" data-testid="stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[170px]" data-testid="source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="companies_house">Companies House</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Table className="min-w-[940px]">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Business / Contact</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>Follow-up</TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => setSortDue((v) => !v)} data-testid="sort-accounts-due" title="Sort by soonest accounts deadline">
                <span className="inline-flex items-center gap-1">Accounts due <CalendarClock className={`h-3.5 w-3.5 ${sortDue ? "text-indigo-600" : "text-slate-400"}`} /></span>
              </TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => {
              const sm = stageMeta(e.stage);
              return (
                <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`} className="group">
                  <TableCell>
                    <p className="flex items-center gap-1.5 font-semibold text-slate-900">
                      {e.business_name}
                      {e.source === "companies_house" && (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700">CH Lead</span>
                      )}
                      {e.source === "website" && (
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700">Website</span>
                      )}
                      {e.enriched_at && <CheckCircle2 className="h-3.5 w-3.5 text-indigo-500" title="Enriched" />}
                      {e.score_tier && (
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${TIERS[e.score_tier] || TIERS.cold}`} data-testid={`score-${e.id}`} title={`Lead score ${e.score}`}>{e.score_tier}</span>
                      )}
                      {e.ai_draft_at && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700" data-testid={`draft-ready-${e.id}`} title="AI draft ready"><PenLine className="h-2.5 w-2.5" /> draft</span>
                      )}
                      {e.opened_at && <MailOpen className="h-3.5 w-3.5 text-emerald-500" title="Email opened" />}
                      {e.opted_out && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500" title="Opted out"><Ban className="inline h-2.5 w-2.5" /> opted out</span>}
                    </p>
                    <p className="text-xs text-slate-500">{e.contact_name}{e.email ? ` · ${e.email}` : ""}</p>
                    {(e.website || (e.directors || []).length > 0) && (
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
                        {e.website && (
                          <a href={e.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 hover:underline">
                            <Globe className="h-3 w-3" />{e.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                        {(e.directors || []).length > 0 && <span>{e.directors.length} director{e.directors.length > 1 ? "s" : ""}</span>}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(e.services || []).slice(0, 2).map((sv) => <span key={sv} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{sv}</span>)}
                      {(e.services || []).length > 2 && <span className="text-[10px] text-slate-400">+{e.services.length - 2}</span>}
                    </div>
                  </TableCell>
                  <TableCell><span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${sm.badge}`}>{sm.label}</span></TableCell>
                  <TableCell><TeamAvatar member={memberById(e.assignee_id)} /></TableCell>
                  <TableCell>{e.follow_up_date ? <FollowUpBadge date={e.follow_up_date} /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell>
                    {(() => {
                      if (!e.accounts_next_due) return <span className="text-xs text-slate-400">—</span>;
                      const d = new Date(e.accounts_next_due);
                      const days = Math.ceil((d - new Date()) / 86400000);
                      const cls = days < 0 ? "bg-red-100 text-red-700" : days <= 14 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
                      const label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                      return (
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`due-${e.id}`} title="First accounts filing deadline">
                          <CalendarClock className="h-3 w-3" />{label}{days >= 0 ? ` · ${days}d` : " · overdue"}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right font-mono-fin text-sm">{gbp(e.deal_value)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Enrich from Companies House + web scan" data-testid={`enrich-${e.id}`} onClick={() => enrichOne(e)} disabled={enrichingId === e.id}>
                        {enrichingId === e.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <Sparkles className="h-4 w-4 text-indigo-500" />}
                      </Button>
                      {e.source === "companies_house" ? (
                        (e.email || e.discovered_email) && !e.opted_out && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Prepare email to send from your own mailbox" data-testid={`prepare-${e.id}`} onClick={() => openSinglePrepare(e)}>
                            <Mail className={`h-4 w-4 ${e.seq_step > 0 ? "text-emerald-600" : "text-indigo-500"}`} />
                          </Button>
                        )
                      ) : (
                        (e.email || e.discovered_email) && !e.opted_out && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title={e.outreach_sent_at ? "Outreach already sent — send again" : "Send outreach email"} data-testid={`outreach-${e.id}`} onClick={() => setOutreachTarget(e)}>
                            <Send className={`h-4 w-4 ${e.outreach_sent_at ? "text-emerald-600" : "text-slate-500"}`} />
                          </Button>
                        )
                      )}
                      {!["intro_call", "active_client", "lost"].includes(e.stage) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Mark as replied → move to Intro Call" data-testid={`replied-${e.id}`} onClick={() => markReplied(e)}>
                          <Reply className="h-4 w-4 text-sky-600" />
                        </Button>
                      )}
                      {e.stage !== "active_client" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Convert to client" data-testid={`convert-client-${e.id}`} onClick={() => convertToClient(e)}>
                          <UserPlus className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" data-testid={`edit-enquiry-${e.id}`} onClick={() => { setEditing(e); setOpen(true); }}>
                        <Pencil className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Delete" data-testid={`delete-enquiry-${e.id}`} onClick={() => setDelTarget(e)}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500" data-testid="enquiries-empty">No enquiries match your filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <EnquiryDialog open={open} onOpenChange={setOpen} onSave={save} team={team} initial={editing} onEnriched={load} />

      <PrepareEmailDialog
        open={!!prepareTarget}
        onOpenChange={(o) => { if (!o) { setPrepareQueue([]); setPrepareIndex(0); } }}
        lead={prepareTarget}
        onSent={advancePrepare}
        batchInfo={prepareQueue.length > 1 ? { current: prepareIndex + 1, total: prepareQueue.length } : null}
      />

      <AlertDialog open={!!outreachTarget} onOpenChange={(o) => !o && setOutreachTarget(null)}>
        <AlertDialogContent data-testid="outreach-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send cold outreach email?</AlertDialogTitle>
            <AlertDialogDescription>
              A personalised introduction email will be sent to{" "}
              <span className="font-semibold text-slate-900">{outreachTarget?.email || outreachTarget?.discovered_email}</span>
              {" "}for {outreachTarget?.business_name}. Edit the template any time in Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOutreach} disabled={sendingOutreach} data-testid="confirm-outreach" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">
              {sendingOutreach ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Send email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>{delTarget?.business_name} will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-enquiry" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
